import { useState, useEffect } from "react";
import { Briefcase, Building2, MapPin, Calendar, Clock, Euro, History } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/config";

const STATUS_META = {
  planned: { label: "Planifiée", cls: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
  in_progress: { label: "En cours", cls: "bg-[#F0E9F3] text-[#662483]", dot: "bg-[#662483]" },
  completed: { label: "Terminée", cls: "bg-green-100 text-green-800", dot: "bg-green-500" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-800", dot: "bg-red-500" },
};

function daysBetween(a, b) {
  if (!a) return 0;
  const start = new Date(a);
  const end = b ? new Date(b) : new Date();
  if (Number.isNaN(start.getTime())) return 0;
  return Math.max(0, Math.round((end - start) / 86400000));
}
function humanDuration(d) {
  if (d < 30) return `${d} j`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m} mois`;
  const y = Math.floor(m / 12);
  const rm = m % 12;
  return `${y} an${y > 1 ? "s" : ""}${rm > 0 ? ` ${rm} mois` : ""}`;
}
function formatDateFR(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Read-only view of the candidate's own missions timeline.
 * Fetched from GET /api/me/missions (notes are hidden server-side).
 */
export default function CandidateMissionsSection() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/me/missions`, { withCredentials: true })
      .then((r) => { if (!cancelled) setMissions(r.data || []); })
      .catch(() => { if (!cancelled) setMissions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const totalDays = missions.reduce((sum, m) => sum + daysBetween(m.start_date, m.end_date), 0);

  return (
    <div className="bg-white border border-gray-200 p-6 mb-8" data-testid="my-missions-section">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-[#662483]" />
        <h2 className="text-lg font-bold text-[#3F3F3F]">Mes missions</h2>
        <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none font-bold ml-1">{missions.length}</Badge>
        {totalDays > 0 && (
          <span className="text-xs text-gray-500 ml-auto">
            <Clock className="w-3 h-3 inline mr-1" />
            {humanDuration(totalDays)} cumulé{totalDays > 30 ? "s" : ""}
          </span>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 italic">Chargement…</p>
      ) : missions.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500" data-testid="my-missions-empty">
          <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          Vous n&apos;avez pas encore de mission enregistrée. Elle apparaîtra ici dès qu&apos;une mission vous sera attribuée.
        </div>
      ) : (
        <div className="relative pl-6" data-testid="my-missions-list">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200" aria-hidden="true" />
          {missions.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.in_progress;
            const days = daysBetween(m.start_date, m.end_date);
            return (
              <div key={m.mission_id} className="relative pb-4" data-testid={`my-mission-${m.mission_id}`}>
                <span className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 border-white shadow ${meta.dot}`} aria-hidden="true" />
                <div className="border border-gray-200 p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 className="w-3.5 h-3.5 text-[#662483]" />
                    <span className="text-sm font-bold text-[#3F3F3F]">{m.company_name}</span>
                    <Badge className={`rounded-none text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mt-1">
                    <Briefcase className="w-3 h-3 inline mr-1 text-gray-400" />
                    {m.job_title}
                  </p>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-2 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDateFR(m.start_date)}{m.end_date ? ` → ${formatDateFR(m.end_date)}` : " → en cours"}</span>
                    {days > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{humanDuration(days)}</span>}
                    {m.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</span>}
                    {m.hourly_rate != null && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{m.hourly_rate}€/h</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
